import { chunk, filter, find, getOr, pipe, propEq, some } from 'lodash/fp';
import { useCallback, useMemo } from 'react';
import { ComponentsVariants } from '@/components.variants';
import { ColorNames } from '@/utils/constants';
import { whenTheme } from '@/utils/theme';
import { TabbedCardTabPanelProps } from './TabbedCard.interfaces';

export const useTabbedCardTabPanelLogic = ({ tab, theme }: TabbedCardTabPanelProps) => {
  const cardColor = useMemo(
    () =>
      whenTheme(
        theme,
        () => ColorNames.eggshell,
        () => ColorNames.charcoal,
      ),
    [theme],
  );

  const cards: DetailContentType<any>[] = useMemo(
    () =>
      pipe<DetailContentType<any>[], DetailContentType<any>[], DetailContentType<any>[]>(
        getOr([], ['tabDetailContent', 'detailContent']),
        filter((variantItem: any) => variantItem?.variant === ComponentsVariants.CARD),
      )(tab),
    [tab],
  );

  const hasFeatured = useMemo(() => some((card: any) => card?.featuredContent, cards), [cards]);

  const cardSections = useMemo(() => {
    if (!hasFeatured) return [];

    const sections: any[] = [];
    let temporalSection: any[] = [];
    cards.forEach((card: any, index) => {
      const isLast = index === cards.length - 1;
      if (card?.featuredContent || isLast) {
        if (isLast) {
          temporalSection.push(card);
        }
        sections.push([...temporalSection]);
        temporalSection = [];
        temporalSection.push(card);
      } else {
        temporalSection.push(card);
      }
    });
    return sections;
  }, [cards, hasFeatured]);

  const parseCardSection = useCallback((sectionCards: any[]) => {
    const featuredCard = find(propEq('featuredContent', true), sectionCards);
    const chunkedRestCards = pipe(
      filter(propEq('featuredContent', false)), // getting non-featured cards
      chunk(3), // chunking in groups of 3
    )(sectionCards);

    return chunkedRestCards.map((chunkedCardRow, index) => {
      if (index === 0) return [featuredCard, ...chunkedCardRow];
      return [{}, ...chunkedCardRow];
    });
  }, []);

  return { cards, cardSections, hasFeatured, cardColor, parseCardSection };
};
